import type { Endpoint, EndpointSection, EndpointSubsection, Product } from '../data';
import { endpointsJa } from './endpoints.ja';
import { endpointsKo } from './endpoints.ko';
import { endpointsZhHK } from './endpoints.zh-hk';
import type { EndpointOverlay } from './overlay';

export type Locale = 'en' | 'zh-hk' | 'ja' | 'ko';

export const LOCALES: Locale[] = ['en', 'zh-hk', 'ja', 'ko'];

export const SITE_URL = 'https://docs.eroya.co';

export type LocaleInfo = {
  /** Value for the <html lang> attribute. */
  htmlLang: string;
  /** Primary hreflang annotation. */
  hreflang: string;
  /** Extra hreflang values resolving to the same document. */
  hreflangAliases: string[];
  /** URL segment; the default locale is unprefixed. */
  segment: string;
  /** Endonym shown in the language menu. */
  label: string;
  /** Short code shown on the collapsed language button. */
  short: string;
};

// Mirrors `localeInfo` on the apex marketing site so the two hreflang graphs
// agree. zh-Hant lets Taiwan and Macau reuse the Hong Kong build.
export const localeInfo: Record<Locale, LocaleInfo> = {
  en: {
    htmlLang: 'en',
    hreflang: 'en',
    hreflangAliases: [],
    segment: '',
    label: 'English',
    short: 'EN',
  },
  'zh-hk': {
    htmlLang: 'zh-Hant-HK',
    hreflang: 'zh-HK',
    hreflangAliases: ['zh-Hant'],
    segment: '/zh-hk',
    label: '繁體中文',
    short: '繁',
  },
  ja: {
    htmlLang: 'ja',
    hreflang: 'ja',
    hreflangAliases: [],
    segment: '/ja',
    label: '日本語',
    short: 'JA',
  },
  ko: {
    htmlLang: 'ko',
    hreflang: 'ko',
    hreflangAliases: [],
    segment: '/ko',
    label: '한국어',
    short: 'KO',
  },
};

const LOCALE_SEGMENT = /^\/(zh-hk|ja|ko)(?=\/|$)/;

/** Derive the active locale from a pathname. */
export function localeFromPath(pathname: string): Locale {
  const match = LOCALE_SEGMENT.exec(pathname);
  return (match?.[1] as Locale) ?? 'en';
}

/** Strip the leading locale segment, returning the locale-neutral path. */
export function stripLocale(pathname: string): string {
  return pathname.replace(LOCALE_SEGMENT, '') || '/';
}

/** Prefix a locale-neutral path with the locale segment (English stays at root). */
export function withLocale(path: string, locale: Locale): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return `${localeInfo[locale].segment}${clean === '/' ? '' : clean}` || '/';
}

/** Absolute canonical URL for a locale-neutral path. */
export function canonicalUrl(path: string, locale: Locale): string {
  return `${SITE_URL}${withLocale(path, locale)}`;
}

type UiStrings = {
  docsLabel: string;
  dataStore: string;
  marketAssetClass: string;
  queryTools: string;
  datasetsLabel: string;
  productLabel: string;
  products: Record<Product, string>;
  sections: Record<EndpointSection, string>;
  subsections: Record<EndpointSubsection, string>;
  groups: Record<Endpoint['group'], string>;
  apiOperational: string;
  status: string;
  searchTrigger: string;
  searchPlaceholder: string;
  searchHint: string;
  noMatch: (query: string) => string;
  copy: string;
  copied: string;
  copyUrl: string;
  paramColParameter: string;
  paramColType: string;
  paramColDescription: string;
  required: string;
  example: string;
  showAllFields: (count: number) => string;
  showFewer: string;
  restApi: string;
  useCases: string;
  parameters: string;
  total: (count: number) => string;
  response: string;
  applicationJson: string;
  implementationNotes: string;
  previous: string;
  next: string;
  requestLabel: string;
  /** Label for the paste-into-the-address-bar request tab. */
  browserTab: string;
  /** Hint shown with the browser request. */
  browserHint: string;
  responseLabel: string;
  authentication: string;
  bearerKey: string;
  authDescription: string;
  manageKeys: string;
  console: string;
  languageLabel: string;
  titleSuffix: string;
  /** <meta name="description"> for the whole reference, per locale. */
  metaDescription: string;
  openNav: string;
  closeNav: string;
  useTheme: (next: string) => string;
};

export const ui: Record<Locale, UiStrings> = {
  en: {
    docsLabel: 'DOCS',
    dataStore: 'Data store',
    marketAssetClass: 'MARKET ASSET CLASS',
    queryTools: 'QUERY & DOWNLOAD',
    datasetsLabel: 'DATASETS',
    productLabel: 'Product',
    products: { rest: 'REST API', 'flat-files': 'Flat Files', websocket: 'WebSocket' },
    sections: {
      'Reference Data': 'Reference Data',
      'Aggregates (OHLC)': 'Aggregates (OHLC)',
      Snapshots: 'Snapshots',
      'Trades & Quotes': 'Trades & Quotes',
      'Prices & Activity': 'Prices & Activity',
      'Company Fundamentals': 'Company Fundamentals',
      'Estimates & Analyst Coverage': 'Estimates & Analyst Coverage',
      'Ownership & Disclosures': 'Ownership & Disclosures',
      'Corporate Actions & Calendars': 'Corporate Actions & Calendars',
      'Market Analytics': 'Market Analytics',
      'News & Content': 'News & Content',
      Derivatives: 'Derivatives',
      'Flat Files': 'Flat Files',
      Streaming: 'Streaming',
    },
    subsections: {
      'Securities & Tickers': 'Securities & Tickers',
      'Trading Sessions': 'Trading Sessions',
      Taxonomies: 'Taxonomies',
      'Aggregate Bars (OHLC)': 'Aggregate Bars (OHLC)',
      'Intraday & Snapshots': 'Intraday & Snapshots',
      'Trades & Quotes': 'Trades & Quotes',
      'Technical Indicators': 'Technical Indicators',
      'Financial Statements': 'Financial Statements',
      'Metrics & Ratios': 'Metrics & Ratios',
      Valuation: 'Valuation',
      'Company Profile': 'Company Profile',
      'Funds & ETFs': 'Funds & ETFs',
      'Consensus Estimates': 'Consensus Estimates',
      'Estimate Revisions': 'Estimate Revisions',
      'Ratings & Price Targets': 'Ratings & Price Targets',
      'Earnings Results': 'Earnings Results',
      Shareholders: 'Shareholders',
      'Insider Activity': 'Insider Activity',
      Filings: 'Filings',
      'Short Interest': 'Short Interest',
      Sustainability: 'Sustainability',
      'Dividends & Buybacks': 'Dividends & Buybacks',
      'Corporate Actions': 'Corporate Actions',
      'Event Calendars': 'Event Calendars',
      'Capital Flow': 'Capital Flow',
      'Market Sentiment': 'Market Sentiment',
      'Trading Statistics': 'Trading Statistics',
      'Broker Holdings': 'Broker Holdings',
      'Cross-Listing (A/H)': 'Cross-Listing (A/H)',
      'Index Composition': 'Index Composition',
      Macroeconomics: 'Macroeconomics',
      'Quantitative Factors': 'Quantitative Factors',
      'Company News': 'Company News',
      Topics: 'Topics',
      Options: 'Options',
      Warrants: 'Warrants',
      'Query & Download': 'Query & Download',
      'Stock Archives': 'Stock Archives',
      'Options Archives': 'Options Archives',
      'Index Archives': 'Index Archives',
      Connecting: 'Connecting',
      'Stock Streams': 'Stock Streams',
      'Options Streams': 'Options Streams',
      'Index Streams': 'Index Streams',
    },
    groups: {
      'U.S. Stocks': 'U.S. Stocks',
      'U.S. Options': 'U.S. Options',
      'U.S. Indices': 'U.S. Indices',
      'Hong Kong Stocks': 'Hong Kong Stocks',
      'China A-shares': 'China A-shares',
      'Global Markets': 'Global Markets',
      'Flat Files': 'Flat Files',
      WebSocket: 'WebSocket',
    },
    apiOperational: 'API operational',
    status: 'Status',
    searchTrigger: 'Search documentation',
    searchPlaceholder: 'Search endpoints and parameters',
    searchHint: 'Search covers endpoint names, paths, descriptions, and parameters.',
    noMatch: (query) => `No endpoint matches “${query}”.`,
    copy: 'Copy',
    copied: 'Copied',
    copyUrl: 'Copy URL',
    paramColParameter: 'Parameter',
    paramColType: 'Type',
    paramColDescription: 'Description',
    required: 'REQUIRED',
    example: 'Example:',
    showAllFields: (count) => `Show all ${count} fields`,
    showFewer: 'Show fewer fields',
    restApi: 'REST API',
    useCases: 'USE CASES',
    parameters: 'Parameters',
    total: (count) => `${count} total`,
    response: 'Response',
    applicationJson: 'application/json',
    implementationNotes: 'IMPLEMENTATION NOTES',
    previous: 'PREVIOUS',
    next: 'NEXT',
    requestLabel: 'REQUEST',
    browserTab: 'Browser',
    browserHint: 'Replace YOUR_EROYA_API_KEY and open the URL in any browser.',
    responseLabel: 'RESPONSE · 200',
    authentication: 'AUTHENTICATION',
    bearerKey: 'Bearer API key',
    authDescription: 'Send your Eroya key in the Authorization header. Query-string apiKey is also supported.',
    manageKeys: 'Manage API keys',
    console: 'CONSOLE',
    languageLabel: 'Language',
    titleSuffix: 'Eroya Docs',
    metaDescription:
      'Eroya API reference for U.S. equities, options, indices, China A-shares, and Analyst Insights.',
    openNav: 'Open navigation',
    closeNav: 'Close navigation',
    useTheme: (next) => `Use ${next} theme`,
  },
  'zh-hk': {
    docsLabel: '文件',
    dataStore: '資料商店',
    marketAssetClass: '市場品種',
    queryTools: '查詢與下載',
    datasetsLabel: '數據集',
    productLabel: '產品',
    products: { rest: 'REST API', 'flat-files': '批量檔案', websocket: 'WebSocket' },
    sections: {
      'Reference Data': '參考資料',
      'Aggregates (OHLC)': '行情彙總（OHLC）',
      Snapshots: '行情快照',
      'Trades & Quotes': '成交與報價',
      'Prices & Activity': '價格與市場活動',
      'Company Fundamentals': '公司基本面',
      'Estimates & Analyst Coverage': '預測與分析師觀點',
      'Ownership & Disclosures': '持股與披露',
      'Corporate Actions & Calendars': '公司行動與日曆',
      'Market Analytics': '市場分析',
      'News & Content': '新聞與內容',
      Derivatives: '衍生產品',
      'Flat Files': '批量檔案',
      Streaming: '即時串流',
    },
    subsections: {
      'Securities & Tickers': '證券與代碼',
      'Trading Sessions': '交易時段',
      Taxonomies: '分類目錄',
      'Aggregate Bars (OHLC)': '聚合 K 線 (OHLC)',
      'Intraday & Snapshots': '分時與快照',
      'Trades & Quotes': '成交與報價',
      'Technical Indicators': '技術指標',
      'Financial Statements': '財務報表',
      'Metrics & Ratios': '指標與比率',
      Valuation: '估值',
      'Company Profile': '公司概況',
      'Funds & ETFs': '基金與 ETF',
      'Consensus Estimates': '一致預期',
      'Estimate Revisions': '預測修訂',
      'Ratings & Price Targets': '評級與目標價',
      'Earnings Results': '業績結果',
      Shareholders: '股東',
      'Insider Activity': '內部人動向',
      Filings: '申報文件',
      'Short Interest': '沽空數據',
      Sustainability: '可持續發展',
      'Dividends & Buybacks': '派息與回購',
      'Corporate Actions': '公司行動',
      'Event Calendars': '事件日曆',
      'Capital Flow': '資金流向',
      'Market Sentiment': '市場情緒',
      'Trading Statistics': '交易統計',
      'Broker Holdings': '經紀持倉',
      'Cross-Listing (A/H)': 'A/H 兩地上市',
      'Index Composition': '指數成分',
      Macroeconomics: '宏觀經濟',
      'Quantitative Factors': '量化因子',
      'Company News': '公司新聞',
      Topics: '話題',
      Options: '期權',
      Warrants: '認股證',
      'Query & Download': '查詢與下載',
      'Stock Archives': '股票封存',
      'Options Archives': '期權封存',
      'Index Archives': '指數封存',
      Connecting: '連線',
      'Stock Streams': '股票串流',
      'Options Streams': '期權串流',
      'Index Streams': '指數串流',
    },
    groups: {
      'U.S. Stocks': '美股',
      'U.S. Options': '美股期權',
      'U.S. Indices': '美股指數',
      'Hong Kong Stocks': '香港股票',
      'China A-shares': '中國 A 股',
      'Global Markets': '全球市場',
      'Flat Files': '批量檔案',
      WebSocket: 'WebSocket',
    },
    apiOperational: 'API 運作正常',
    status: '服務狀態',
    searchTrigger: '搜尋文件',
    searchPlaceholder: '搜尋接口與參數',
    searchHint: '搜尋範圍涵蓋接口名稱、路徑、說明與參數。',
    noMatch: (query) => `沒有符合「${query}」的接口。`,
    copy: '複製',
    copied: '已複製',
    copyUrl: '複製連結',
    paramColParameter: '參數',
    paramColType: '類型',
    paramColDescription: '說明',
    required: '必填',
    example: '示例：',
    showAllFields: (count) => `顯示全部 ${count} 個欄位`,
    showFewer: '收合欄位',
    restApi: 'REST API',
    useCases: '應用場景',
    parameters: '參數',
    total: (count) => `共 ${count} 項`,
    response: '回應',
    applicationJson: 'application/json',
    implementationNotes: '實作說明',
    previous: '上一個',
    next: '下一個',
    requestLabel: '請求',
    browserTab: '瀏覽器',
    browserHint: '把 YOUR_EROYA_API_KEY 換成你的金鑰，即可在瀏覽器直接開啟這條網址測試。',
    responseLabel: '回應 · 200',
    authentication: '認證',
    bearerKey: 'Bearer API 金鑰',
    authDescription: '在 Authorization 標頭中傳入你的 Eroya 金鑰，也支援使用 apiKey 查詢參數。',
    manageKeys: '管理 API 金鑰',
    console: '控制台',
    languageLabel: '語言',
    titleSuffix: 'Eroya 文件',
    metaDescription:
      'Eroya 市場資料 API 文件：美股財務報表數據 API、美股期權、美股指數與中國 A 股接口，適用於量化回測數據集與 Point-in-Time 數據庫。',
    openNav: '開啟導覽',
    closeNav: '關閉導覽',
    useTheme: (next) => `切換為${next}主題`,
  },
  ja: {
    docsLabel: 'ドキュメント',
    dataStore: 'データストア',
    marketAssetClass: 'マーケット資産クラス',
    queryTools: '検索とダウンロード',
    datasetsLabel: 'データセット',
    productLabel: '製品',
    products: { rest: 'REST API', 'flat-files': 'フラットファイル', websocket: 'WebSocket' },
    sections: {
      'Reference Data': 'リファレンスデータ',
      'Aggregates (OHLC)': '集計バー（OHLC）',
      Snapshots: 'スナップショット',
      'Trades & Quotes': '約定と気配値',
      'Prices & Activity': '価格と市場動向',
      'Company Fundamentals': '企業ファンダメンタルズ',
      'Estimates & Analyst Coverage': '予想とアナリストカバレッジ',
      'Ownership & Disclosures': '保有と開示',
      'Corporate Actions & Calendars': 'コーポレートアクションとカレンダー',
      'Market Analytics': 'マーケット分析',
      'News & Content': 'ニュースとコンテンツ',
      Derivatives: 'デリバティブ',
      'Flat Files': 'フラットファイル',
      Streaming: 'ストリーミング',
    },
    subsections: {
      'Securities & Tickers': '銘柄とティッカー',
      'Trading Sessions': '取引セッション',
      Taxonomies: '分類マスタ',
      'Aggregate Bars (OHLC)': '集計バー（OHLC）',
      'Intraday & Snapshots': '日中足とスナップショット',
      'Trades & Quotes': '約定と気配',
      'Technical Indicators': 'テクニカル指標',
      'Financial Statements': '財務諸表',
      'Metrics & Ratios': '指標と比率',
      Valuation: 'バリュエーション',
      'Company Profile': '企業概要',
      'Funds & ETFs': 'ファンドとETF',
      'Consensus Estimates': 'コンセンサス予想',
      'Estimate Revisions': '予想修正',
      'Ratings & Price Targets': 'レーティングと目標株価',
      'Earnings Results': '決算実績',
      Shareholders: '株主',
      'Insider Activity': 'インサイダー動向',
      Filings: '提出書類',
      'Short Interest': '空売り残高',
      Sustainability: 'サステナビリティ',
      'Dividends & Buybacks': '配当と自社株買い',
      'Corporate Actions': 'コーポレートアクション',
      'Event Calendars': 'イベントカレンダー',
      'Capital Flow': '資金流向',
      'Market Sentiment': '市場センチメント',
      'Trading Statistics': '売買統計',
      'Broker Holdings': 'ブローカー保有',
      'Cross-Listing (A/H)': 'A/H二重上場',
      'Index Composition': '指数構成',
      Macroeconomics: 'マクロ経済',
      'Quantitative Factors': 'クオンツファクター',
      'Company News': '企業ニュース',
      Topics: 'トピック',
      Options: 'オプション',
      Warrants: 'ワラント',
      'Query & Download': '照会とダウンロード',
      'Stock Archives': '株式アーカイブ',
      'Options Archives': 'オプションアーカイブ',
      'Index Archives': '指数アーカイブ',
      Connecting: '接続',
      'Stock Streams': '株式ストリーム',
      'Options Streams': 'オプションストリーム',
      'Index Streams': '指数ストリーム',
    },
    groups: {
      'U.S. Stocks': '米国株',
      'U.S. Options': '米国オプション',
      'U.S. Indices': '米国株価指数',
      'Hong Kong Stocks': '香港株',
      'China A-shares': '中国 A 株',
      'Global Markets': 'グローバル市場',
      'Flat Files': 'フラットファイル',
      WebSocket: 'WebSocket',
    },
    apiOperational: 'API 正常稼働中',
    status: 'ステータス',
    searchTrigger: 'ドキュメントを検索',
    searchPlaceholder: 'エンドポイントとパラメータを検索',
    searchHint: '検索対象はエンドポイント名、パス、説明、パラメータです。',
    noMatch: (query) => `「${query}」に一致するエンドポイントはありません。`,
    copy: 'コピー',
    copied: 'コピーしました',
    copyUrl: 'URL をコピー',
    paramColParameter: 'パラメータ',
    paramColType: '型',
    paramColDescription: '説明',
    required: '必須',
    example: '例：',
    showAllFields: (count) => `${count} 件のフィールドをすべて表示`,
    showFewer: 'フィールドを折りたたむ',
    restApi: 'REST API',
    useCases: 'ユースケース',
    parameters: 'パラメータ',
    total: (count) => `全 ${count} 件`,
    response: 'レスポンス',
    applicationJson: 'application/json',
    implementationNotes: '実装上の注意',
    previous: '前へ',
    next: '次へ',
    requestLabel: 'リクエスト',
    browserTab: 'ブラウザ',
    browserHint: 'YOUR_EROYA_API_KEY を自分のキーに置き換えて、そのままブラウザで開けます。',
    responseLabel: 'レスポンス · 200',
    authentication: '認証',
    bearerKey: 'Bearer API キー',
    authDescription:
      'Authorization ヘッダーに Eroya の API キーを指定します。クエリ文字列の apiKey にも対応しています。',
    manageKeys: 'API キーを管理',
    console: 'コンソール',
    languageLabel: '言語',
    titleSuffix: 'Eroya ドキュメント',
    metaDescription:
      '米国株・米国オプション・米国株価指数・中国 A 株を対象とした Eroya マーケットデータ API リファレンス。ファンダメンタルズ API とクオンツ バックテスト データのエンドポイント仕様。',
    openNav: 'ナビゲーションを開く',
    closeNav: 'ナビゲーションを閉じる',
    useTheme: (next) => `${next}テーマに切り替える`,
  },
  ko: {
    docsLabel: '문서',
    dataStore: '데이터 스토어',
    marketAssetClass: '시장 자산군',
    queryTools: '조회 및 다운로드',
    datasetsLabel: '데이터셋',
    productLabel: '제품',
    products: { rest: 'REST API', 'flat-files': '플랫 파일', websocket: 'WebSocket' },
    sections: {
      'Reference Data': '참조 데이터',
      'Aggregates (OHLC)': '집계 바(OHLC)',
      Snapshots: '스냅샷',
      'Trades & Quotes': '체결·호가',
      'Prices & Activity': '가격·시장 활동',
      'Company Fundamentals': '기업 펀더멘털',
      'Estimates & Analyst Coverage': '추정치·애널리스트 커버리지',
      'Ownership & Disclosures': '지분·공시',
      'Corporate Actions & Calendars': '기업 활동·캘린더',
      'Market Analytics': '시장 분석',
      'News & Content': '뉴스·콘텐츠',
      Derivatives: '파생상품',
      'Flat Files': '플랫 파일',
      Streaming: '스트리밍',
    },
    subsections: {
      'Securities & Tickers': '종목·티커',
      'Trading Sessions': '거래 세션',
      Taxonomies: '분류 체계',
      'Aggregate Bars (OHLC)': '집계 바(OHLC)',
      'Intraday & Snapshots': '장중·스냅샷',
      'Trades & Quotes': '체결·호가',
      'Technical Indicators': '기술적 지표',
      'Financial Statements': '재무제표',
      'Metrics & Ratios': '지표·비율',
      Valuation: '밸류에이션',
      'Company Profile': '기업 개요',
      'Funds & ETFs': '펀드·ETF',
      'Consensus Estimates': '컨센서스 추정치',
      'Estimate Revisions': '추정치 수정',
      'Ratings & Price Targets': '투자의견·목표주가',
      'Earnings Results': '실적',
      Shareholders: '주주',
      'Insider Activity': '내부자 동향',
      Filings: '공시 서류',
      'Short Interest': '공매도 잔고',
      Sustainability: '지속가능성',
      'Dividends & Buybacks': '배당·자사주 매입',
      'Corporate Actions': '기업 활동',
      'Event Calendars': '이벤트 캘린더',
      'Capital Flow': '자금 흐름',
      'Market Sentiment': '시장 심리',
      'Trading Statistics': '매매 통계',
      'Broker Holdings': '브로커 보유',
      'Cross-Listing (A/H)': 'A/H 교차상장',
      'Index Composition': '지수 구성',
      Macroeconomics: '거시경제',
      'Quantitative Factors': '퀀트 팩터',
      'Company News': '기업 뉴스',
      Topics: '토픽',
      Options: '옵션',
      Warrants: '워런트',
      'Query & Download': '조회·다운로드',
      'Stock Archives': '주식 아카이브',
      'Options Archives': '옵션 아카이브',
      'Index Archives': '지수 아카이브',
      Connecting: '연결',
      'Stock Streams': '주식 스트림',
      'Options Streams': '옵션 스트림',
      'Index Streams': '지수 스트림',
    },
    groups: {
      'U.S. Stocks': '미국 주식',
      'U.S. Options': '미국 옵션',
      'U.S. Indices': '미국 지수',
      'Hong Kong Stocks': '홍콩 주식',
      'China A-shares': '중국 A주',
      'Global Markets': '글로벌 시장',
      'Flat Files': '플랫 파일',
      WebSocket: 'WebSocket',
    },
    apiOperational: 'API 정상 작동',
    status: '상태',
    searchTrigger: '문서 검색',
    searchPlaceholder: '엔드포인트와 파라미터 검색',
    searchHint: '엔드포인트 이름, 경로, 설명, 파라미터를 검색합니다.',
    noMatch: (query) => `“${query}”와 일치하는 엔드포인트가 없습니다.`,
    copy: '복사',
    copied: '복사됨',
    copyUrl: 'URL 복사',
    paramColParameter: '파라미터',
    paramColType: '타입',
    paramColDescription: '설명',
    required: '필수',
    example: '예시:',
    showAllFields: (count) => `필드 ${count}개 모두 보기`,
    showFewer: '필드 접기',
    restApi: 'REST API',
    useCases: '활용 사례',
    parameters: '파라미터',
    total: (count) => `총 ${count}개`,
    response: '응답',
    applicationJson: 'application/json',
    implementationNotes: '구현 참고 사항',
    previous: '이전',
    next: '다음',
    requestLabel: '요청',
    browserTab: '브라우저',
    browserHint: 'YOUR_EROYA_API_KEY를 본인 키로 바꾸면 브라우저에서 바로 열어 볼 수 있습니다.',
    responseLabel: '응답 · 200',
    authentication: '인증',
    bearerKey: 'Bearer API 키',
    authDescription:
      'Authorization 헤더에 Eroya API 키를 담아 보내세요. 쿼리 문자열 apiKey도 지원합니다.',
    manageKeys: 'API 키 관리',
    console: '콘솔',
    languageLabel: '언어',
    titleSuffix: 'Eroya 문서',
    metaDescription:
      '미국 주식·옵션·지수·중국 A주를 위한 Eroya 마켓 데이터 API 레퍼런스. 미국 주식 재무제표 데이터와 퀀트 투자 데이터셋 API 엔드포인트 명세.',
    openNav: '내비게이션 열기',
    closeNav: '내비게이션 닫기',
    useTheme: (next) => `${next} 테마로 전환`,
  },
};

export const themeLabel: Record<Locale, { light: string; dark: string }> = {
  en: { light: 'light', dark: 'dark' },
  'zh-hk': { light: '淺色', dark: '深色' },
  ja: { light: 'ライト', dark: 'ダーク' },
  ko: { light: '라이트', dark: '다크' },
};

const overlays: Record<Exclude<Locale, 'en'>, Record<string, EndpointOverlay>> = {
  'zh-hk': endpointsZhHK,
  ja: endpointsJa,
  ko: endpointsKo,
};

/** Return an endpoint with its narrative and parameter descriptions localized. */
export function localizeEndpoint(endpoint: Endpoint, locale: Locale): Endpoint {
  if (locale === 'en') return endpoint;
  const overlay = overlays[locale][endpoint.id];
  if (!overlay) return endpoint;
  return {
    ...endpoint,
    title: overlay.title,
    summary: overlay.summary,
    description: overlay.description,
    useCases: overlay.useCases,
    notes: overlay.notes ?? endpoint.notes,
    parameters: endpoint.parameters.map((parameter) => ({
      ...parameter,
      description: overlay.params?.[parameter.name] ?? parameter.description,
    })),
  };
}
